import React, { useContext, useEffect, useState } from 'react';
import { DialogContentText, Button } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { useTheme } from '@mui/material/styles';

import { IconWrapper, StyledIconCircle } from '../UpdateWarningModal/styles';
import { useTranslation } from '../../context/TranslationContext';
import { useBackendStatusContext } from '../../context/backendStatus/backendStatusContext';
import { useCompatibilityMatrix } from '../../context/compatibilityMatrixContext';
import { useSeenVersion } from '../../hooks/useSeenVersion';
import appInfo from '../../../package.json';
import { useAuthContext } from '../../context/AuthContext';

import {
  StyledDialog,
  StyledTitle,
  ChangelogBox,
  ChangelogTitle,
  StyledDialogContent,
  StyledDialogActions,
} from './styles';
import ChangelogItem from './ChangelogItem';
import { mergeChangelogs } from './helpers';

const UpdateInstalledModal = () => {
  const [open, setOpen] = useState(false);
  const { translate } = useTranslation();
  const theme = useTheme();

  const { state: backendState } = useBackendStatusContext();
  const { matrix } = useCompatibilityMatrix();
  const { userData } = useAuthContext();
  const userId = userData.sub;

  const backendVersion = backendState.version;
  const frontendVersion = appInfo.version;
  const isUpdating = backendState.isUpdating;

  const { isVersionAlreadySeen, saveVersionAsSeen } = useSeenVersion({
    userId,
    frontendVersion,
    backendVersion,
  });

  const combinedChangelog = mergeChangelogs(matrix, frontendVersion, backendVersion);

  useEffect(() => {
    if (!userId || !frontendVersion || !backendVersion || isUpdating) return;

    if (!isVersionAlreadySeen()) {
      setOpen(true);
    }
  }, [userId, frontendVersion, backendVersion, isUpdating, isVersionAlreadySeen]);

  const handleClose = () => {
    saveVersionAsSeen();
    setOpen(false);
  };

  return (
    <StyledDialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose();
      }}
      aria-labelledby="update-installed-title"
    >
      <IconWrapper>
        <StyledIconCircle sx={{ backgroundColor: theme.palette.primary.main }}>
          <DoneIcon sx={{ fontSize: 60 }} />
        </StyledIconCircle>
      </IconWrapper>
      <StyledTitle variant="h5">{translate('update.updateInstalled')}</StyledTitle>
      <StyledDialogContent>
        <DialogContentText
          sx={{
            mb: 1,
          }}
        >
          {translate('update.deviceUpdatedSuccessfully')}
        </DialogContentText>
        {combinedChangelog.length > 0 && (
          <>
            <ChangelogTitle>{translate('update.whatsNew')}:</ChangelogTitle>
            <ChangelogBox>
              {combinedChangelog.map((item, index) => (
                <ChangelogItem key={index} item={item} />
              ))}
            </ChangelogBox>
          </>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <Button variant="contained" onClick={handleClose}>
          OK
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default UpdateInstalledModal;
